import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "getting-started-with-adobe-experience-platform"
    }}>{`Getting started with Adobe Experience Platform`}</h1>
    <p>{`This tutorial illustrates how you may send Experience Data Model (XDM) commerce events to Adobe Experience Platform via Experience Edge using the AEP Edge extension in a sample application, provided to you in iOS (Swift) and Android.`}</p>
    <p>{`The demo mobile application has multiple tabs. For this exercise, the `}<inlineCode parentName="p">{`Edge`}</inlineCode>{` and `}<inlineCode parentName="p">{`Assurance`}</inlineCode>{` tabs will be used, demonstrating XDM commerce events in a mobile application.`}</p>
    <h2 {...{
      "id": "prerequisites-for-this-tutorial"
    }}>{`Prerequisites for this tutorial`}</h2>
    <ul>
      <li parentName="ul">{`Access to Adobe Experience Platform`}</li>
      <li parentName="ul">{`Access to Data Collection UI dashboard`}</li>
      <li parentName="ul">{`Access to Adobe Experience Platform Assurance`}</li>
      <li parentName="ul">{`Minimal Swift/Android development knowledge`}</li>
      <li parentName="ul">{`General knowledge about the Adobe Experience Platform Mobile SDKs`}</li>
    </ul>
    <h2 {...{
      "id": "initialize-adobe-experience-platform-for-data-collection"
    }}>{`Initialize Adobe Experience Platform for data collection`}</h2>
    <p>{`To start collecting data in Adobe Experience Platform, you need to create an XDM schema and a dataset. Follow these steps to get started:`}</p>
    <ol>
      <li parentName="ol">{`In the browser, navigate to `}<a parentName="li" {...{
          "href": "https://experience.adobe.com/platform"
        }}>{`Adobe Experience Platform`}</a>{` and login with your credentials.`}</li>
      <li parentName="ol">{`Create an XDM Schema as follows:`}
        <ul parentName="li">
          <li parentName="ul">{`From the left panel, select Schemas`}</li>
          <li parentName="ul">{`Select `}<inlineCode parentName="li">{`Create schema`}</inlineCode></li>
          <li parentName="ul">{`Select `}<inlineCode parentName="li">{`XDM Experience event`}</inlineCode></li>
          <li parentName="ul">{`Add the following existing field groups:`}
            <ul parentName="li">
              <li parentName="ul">{`Environment Details`}</li>
              <li parentName="ul">{`Commerce Details`}</li>
            </ul>
          </li>
          <li parentName="ul">{`Set a name for this schema and click `}<inlineCode parentName="li">{`Save`}</inlineCode>{`.`}</li>
        </ul>
      </li>
    </ol>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "53.43750000000001%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/b9fdfea50d4cd5a18d540adb3d648532/5530d/xdm-schema-example.webp 320w", "/client-sdks/static/b9fdfea50d4cd5a18d540adb3d648532/0c8fb/xdm-schema-example.webp 640w", "/client-sdks/static/b9fdfea50d4cd5a18d540adb3d648532/94b1e/xdm-schema-example.webp 1280w", "/client-sdks/static/b9fdfea50d4cd5a18d540adb3d648532/0b34d/xdm-schema-example.webp 1920w", "/client-sdks/static/b9fdfea50d4cd5a18d540adb3d648532/d5269/xdm-schema-example.webp 2560w", "/client-sdks/static/b9fdfea50d4cd5a18d540adb3d648532/6e85c/xdm-schema-example.webp 3584w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/b9fdfea50d4cd5a18d540adb3d648532/dd4a7/xdm-schema-example.png 320w", "/client-sdks/static/b9fdfea50d4cd5a18d540adb3d648532/0f09e/xdm-schema-example.png 640w", "/client-sdks/static/b9fdfea50d4cd5a18d540adb3d648532/bbbf7/xdm-schema-example.png 1280w", "/client-sdks/static/b9fdfea50d4cd5a18d540adb3d648532/ac7a9/xdm-schema-example.png 1920w", "/client-sdks/static/b9fdfea50d4cd5a18d540adb3d648532/c7a69/xdm-schema-example.png 2560w", "/client-sdks/static/b9fdfea50d4cd5a18d540adb3d648532/ed6ae/xdm-schema-example.png 3584w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/client-sdks/static/b9fdfea50d4cd5a18d540adb3d648532/bbbf7/xdm-schema-example.png",
            "alt": "xdm schema example",
            "title": "xdm schema example",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p><strong parentName="p">{`Note:`}</strong>{` Please note that the selected sandbox is Prod.`}</p>
    <ol {...{
      "start": 3
    }}>
      <li parentName="ol">{`Create a dataset from the schema as follows:`}
        <ul parentName="li">
          <li parentName="ul">{`From the left panel, select Datasets`}</li>
          <li parentName="ul">{`Select `}<inlineCode parentName="li">{`Create dataset from schema`}</inlineCode></li>
          <li parentName="ul">{`Select the schema you previously created`}</li>
          <li parentName="ul">{`Select `}<inlineCode parentName="li">{`Next`}</inlineCode></li>
          <li parentName="ul">{`Set a name for this dataset and select `}<inlineCode parentName="li">{`Finish`}</inlineCode>{`.`}</li>
        </ul>
      </li>
    </ol>
    <h2 {...{
      "id": "generate-a-datastream-identifier"
    }}>{`Generate a Datastream identifier`}</h2>
    <p>{`The SDK requires a configuration identifier that ensures the implementation matches the server-side Datastream configuration and data is routed/received to/from the correct destination.`}</p>
    <p>{`To create a configuration identifier use the following steps:`}</p>
    <ol>
      <li parentName="ol">{`If your organization is enabled for multiple sandboxes, in `}<a parentName="li" {...{
          "href": "https://experience.adobe.com/#/data-collection"
        }}>{`Adobe Experience Platform Data Collection UI`}</a>{` first select the sandbox from the top right corner. For more details about sandboxes, please read the `}<a parentName="li" {...{
          "href": "https://experienceleague.adobe.com/docs/experience-platform/sandbox/home.html"
        }}>{`sandboxes overview`}</a>{`.`}</li>
      <li parentName="ol">{`From the left panel select `}<inlineCode parentName="li">{`Datastreams`}</inlineCode>{`, then select `}<inlineCode parentName="li">{`New Datastream`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Provide a name and description, select `}<inlineCode parentName="li">{`Save`}</inlineCode>{` and then proceed to next step to set up the services.`}</li>
    </ol>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "36.875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/7c79936683071829f90f2e19a5efc1f2/5530d/create-datastream.webp 320w", "/client-sdks/static/7c79936683071829f90f2e19a5efc1f2/0c8fb/create-datastream.webp 640w", "/client-sdks/static/7c79936683071829f90f2e19a5efc1f2/94b1e/create-datastream.webp 1280w", "/client-sdks/static/7c79936683071829f90f2e19a5efc1f2/0b34d/create-datastream.webp 1920w", "/client-sdks/static/7c79936683071829f90f2e19a5efc1f2/064ce/create-datastream.webp 2267w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/7c79936683071829f90f2e19a5efc1f2/dd4a7/create-datastream.png 320w", "/client-sdks/static/7c79936683071829f90f2e19a5efc1f2/0f09e/create-datastream.png 640w", "/client-sdks/static/7c79936683071829f90f2e19a5efc1f2/bbbf7/create-datastream.png 1280w", "/client-sdks/static/7c79936683071829f90f2e19a5efc1f2/ac7a9/create-datastream.png 1920w", "/client-sdks/static/7c79936683071829f90f2e19a5efc1f2/fd2a9/create-datastream.png 2267w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/client-sdks/static/7c79936683071829f90f2e19a5efc1f2/bbbf7/create-datastream.png",
            "alt": "create datastream",
            "title": "create datastream",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`To send events to Adobe Experience Platform, enable the `}<inlineCode parentName="p">{`Adobe Experience Platform`}</inlineCode>{` section as shown below:`}</p>
        <ul parentName="li">
          <li parentName="ul">
            <p parentName="li">{`Select `}<inlineCode parentName="p">{`Add service`}</inlineCode>{` and from the drop-down select `}<inlineCode parentName="p">{`Adobe Experience Platform`}</inlineCode>{`.`}</p>
          </li>
          <li parentName="ul">
            <p parentName="li">{`For the `}<inlineCode parentName="p">{`Event Dataset`}</inlineCode>{`, select the XDM dataset you created in `}<a parentName="p" {...{
                "href": "../../../edge/edge-network/index.md"
              }}>{`Initialize Adobe Experience Platform for data collection`}</a>{`.`}</p>
          </li>
          <li parentName="ul">
            <p parentName="li">{`Click `}<inlineCode parentName="p">{`Save`}</inlineCode>{`.`}</p>
          </li>
        </ul>
      </li>
    </ol>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "35.3125%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/71146c2b6594ec4118bcad975d538e56/5530d/enable-datastreams.webp 320w", "/client-sdks/static/71146c2b6594ec4118bcad975d538e56/0c8fb/enable-datastreams.webp 640w", "/client-sdks/static/71146c2b6594ec4118bcad975d538e56/94b1e/enable-datastreams.webp 1280w", "/client-sdks/static/71146c2b6594ec4118bcad975d538e56/0b34d/enable-datastreams.webp 1920w", "/client-sdks/static/71146c2b6594ec4118bcad975d538e56/fa17d/enable-datastreams.webp 2262w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/71146c2b6594ec4118bcad975d538e56/dd4a7/enable-datastreams.png 320w", "/client-sdks/static/71146c2b6594ec4118bcad975d538e56/0f09e/enable-datastreams.png 640w", "/client-sdks/static/71146c2b6594ec4118bcad975d538e56/bbbf7/enable-datastreams.png 1280w", "/client-sdks/static/71146c2b6594ec4118bcad975d538e56/ac7a9/enable-datastreams.png 1920w", "/client-sdks/static/71146c2b6594ec4118bcad975d538e56/e9762/enable-datastreams.png 2262w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/client-sdks/static/71146c2b6594ec4118bcad975d538e56/bbbf7/enable-datastreams.png",
            "alt": "enable datastreams",
            "title": "enable datastreams",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h2 {...{
      "id": "configure-the-tags-property"
    }}>{`Configure the tags property`}</h2>
    <p>{`As a prerequisite, the Adobe Experience Platform Edge extension requires the successful implementation of the Adobe Experience Platform Mobile SDK - `}<a parentName="p" {...{
        "href": "../../mobile-core/index.md"
      }}>{`Mobile Core`}</a>{`.`}</p>
    <p>{`Experience Edge extension relies on the `}<a parentName="p" {...{
        "href": "../../../home/base/mobile-core/index.md"
      }}>{`Mobile Core`}</a>{` for the transmission of events, managing identity (ECID), and triggering client-side rules based on XDM.`}</p>
    <ol>
      <li parentName="ol">{`First, follow the steps in the `}<a parentName="li" {...{
          "href": "../../../home/getting-started/create-a-mobile-property.md"
        }}>{`set up a mobile property tutorial`}</a>{` in the Data Collection UI.`}</li>
      <li parentName="ol">{`Install the `}<inlineCode parentName="li">{`Adobe Experience Platform Edge`}</inlineCode>{` extension from the Catalog.`}</li>
      <li parentName="ol">{`In the configuration view, from `}<inlineCode parentName="li">{`Edge Configuration`}</inlineCode>{`  section select the `}<inlineCode parentName="li">{`Datastream`}</inlineCode>{` configuration you created in the `}<inlineCode parentName="li">{`Generate a Datastream identifier`}</inlineCode>{` step and click `}<inlineCode parentName="li">{`Save`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Install the `}<inlineCode parentName="li">{`AEP Assurance`}</inlineCode>{` extension from the Catalog.`}</li>
      <li parentName="ol">{`Go to the Publishing Flow menu, select the development library you created and click `}<inlineCode parentName="li">{`Add All Changed Resources`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Click `}<inlineCode parentName="li">{`Save & Build for Development`}</inlineCode>{` to publish the changes in the `}<strong parentName="li">{`Development`}</strong>{` environment.`}</li>
    </ol>
    <h2 {...{
      "id": "download-the-sample-application-and-the-aep-mobile-extensions"
    }}>{`Download the sample application and the AEP mobile extensions`}</h2>
    <h3 {...{
      "id": "swift"
    }}>{`Swift`}</h3>
    <p>{`Download the iOS Swift Sample application from `}<a parentName="p" {...{
        "href": "https://github.com/adobe/aepsdk-sample-app-ios/releases/tag/beta-assignment-1"
      }}>{`GitHub - beta-assignment-1`}</a>{`, then follow the steps described in `}<a parentName="p" {...{
        "href": "https://github.com/adobe/aepsdk-sample-app-ios/tree/beta-assignment-1#installation"
      }}>{`AEP SDK Sample App Swift - Installation`}</a>{`.`}</p>
    <ul>
      <li parentName="ul">{`Navigate to the `}<inlineCode parentName="li">{`Swift`}</inlineCode>{` directory, and run the following command from terminal: `}<inlineCode parentName="li">{`pod install`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Open the Xcode workspace by running the command `}<inlineCode parentName="li">{`open AEPSampleApp.xcworkspace`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Run the `}<inlineCode parentName="li">{`AEPSampleApp`}</inlineCode>{` target on the simulator of your choice.`}</li>
    </ul>
    <h2 {...{
      "id": "set-up-the-configuration"
    }}>{`Set up the configuration`}</h2>
    <p>{`In the `}<a parentName="p" {...{
        "href": "https://experience.adobe.com/#/data-collection/"
      }}>{`Data Collection UI`}</a>{`, go to the `}<strong parentName="p">{`Environments`}</strong>{` tab in the previously created mobile property created in `}<a parentName="p" {...{
        "href": "#configure-the-tags-property"
      }}>{`the previous step`}</a>{` and select the Development icon (`}<span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "24px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "112.5%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/7c534fd570c081dfd8c36016b8862f6f/ad722/development-icon.webp 24w"],
            "sizes": "(max-width: 24px) 100vw, 24px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/7c534fd570c081dfd8c36016b8862f6f/22424/development-icon.png 24w"],
            "sizes": "(max-width: 24px) 100vw, 24px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/client-sdks/static/7c534fd570c081dfd8c36016b8862f6f/22424/development-icon.png",
            "alt": "img",
            "title": "img",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span>{`). Find the `}<strong parentName="p">{`Environment File ID`}</strong>{` at the top and copy it.`}</p>
    <p>{`Set the `}<inlineCode parentName="p">{`LAUNCH_ENVIRONMENT_FILE_ID`}</inlineCode>{` to the copied Environment File ID in the `}<inlineCode parentName="p">{`MainApp`}</inlineCode>{` (Android) / `}<inlineCode parentName="p">{`AppDelegate`}</inlineCode>{` (iOS) class.`}</p>
    <h2 {...{
      "id": "use-the-sample-application"
    }}>{`Use the sample application`}</h2>
    <h3 {...{
      "id": "adobe-experience-platform-edge-extension-and-xdm-objects"
    }}>{`Adobe Experience Platform Edge extension and XDM objects`}</h3>
    <p>{`This application uses the Adobe Experience Platform Edge extension for sending XDM formatted data to the Adobe Experience Edge Network and so to Adobe Experience Platform. The XDM data is modelled based on the XDM Schemas you have configured in Adobe Experience Platform.`}</p>
    <p>{`The sample app includes automatically generated source classes for the XDM Objects that define the commerce field group configured in the previous step. To explore these, check the `}<inlineCode parentName="p">{`MobileSDKCommerceSchema`}</inlineCode>{` class and its usages in `}<inlineCode parentName="p">{`EdgeViewController.swift`}</inlineCode>{` (iOS) / `}<inlineCode parentName="p">{`EdgeTab.java`}</inlineCode>{` (Android).`}</p>
    <h3 {...{
      "id": "commerce-events"
    }}>{`Commerce events`}</h3>
    <p>{`In the sample app, click on the `}<inlineCode parentName="p">{`Edge`}</inlineCode>{` tab that demonstrates the Commerce field group usage. In the `}<inlineCode parentName="p">{`XDM Commerce Example`}</inlineCode>{` section there are two buttons:`}</p>
    <ul>
      <li parentName="ul">{`Add to cart`}</li>
      <li parentName="ul">{`Purchase`}</li>
    </ul>
    <p>{`When the `}<inlineCode parentName="p">{`Purchase`}</inlineCode>{` button is clicked, a new XDM Commerce Purchase Experience Event is created and sent to the Adobe Experience Edge Network.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "events": [
    {
      "xdm": {
        "_id": "F6BB7EE3-C411-4597-A97F-36A49035DCED",
        "eventType": "commerce.purchases",
        "timestamp": "2020-10-09T00:18:18Z",
        "productListItems": [
          {
            "quantity": 1,
            "currencyCode": "USD",
            "priceTotal": 34.76,
            "SKU": "SHOES123",
            "name": "Shoes"
          },
          {
            "quantity": 2,
            "currencyCode": "USD",
            "priceTotal": 30.6,
            "SKU": "HAT567",
            "name": "Hat"
          }
        ],
        "commerce": {
          "order": {
            "priceTotal": 65.36,
            "currencyCode": "USD",
            "payments": [
              {
                "paymentAmount": 65.36,
                "paymentType": "Credit card"
              }
            ]
          },
          "purchases": {
            "value": 1
          }
        }
      }
    }
  ]
}
`}</code></pre>
    <h3 {...{
      "id": "using-adobe-experience-platform-assurance"
    }}>{`Using Adobe Experience Platform Assurance`}</h3>
    <p>{`AEP Assurance (formerly known as Project Griffon) is a product from Adobe that lets you inspect, validate, and debug data collection and experiences for your mobile application. The demo app is set up to use the AEP Assurance mobile extension, which allows you to view the events being sent through the AEP Mobile SDK.`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Visit `}<a parentName="p" {...{
            "href": "https://experience.adobe.com/assurance"
          }}></a>{` and login with your Adobe credentials.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Create a new Session:`}</p>
        <ul parentName="li">
          <li parentName="ul">{`Select `}<inlineCode parentName="li">{`Create Session`}</inlineCode>{` in the top right.`}</li>
          <li parentName="ul">{`In the `}<inlineCode parentName="li">{`Create New Session`}</inlineCode>{` dialog, review the instructions, and then select `}<inlineCode parentName="li">{`Start`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`When asked for the `}<strong parentName="li">{`Base URL`}</strong>{`, enter `}<inlineCode parentName="li">{`sampleapp://`}</inlineCode>{` , then click `}<inlineCode parentName="li">{`Next`}</inlineCode>{`.`}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`After starting an Assurance session, click on the the Session Details button on the right corner of the Assurance page and copy the session link.`}</p>
        <p parentName="li">{`If you are using a real device, you can also use the `}<inlineCode parentName="p">{`Scan QR Code`}</inlineCode>{` functionality. You can then skip to step 6.`}</p>
        <p parentName="li"><span parentName="p" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "958px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "138.75%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "display": "block",
                "transition": "opacity 0.5s 0.5s",
                "pointerEvents": "none"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/client-sdks/static/c8cc71d0c8f7354ccd90fb4c40d79a6b/5530d/commerces-session-details.webp 320w", "/client-sdks/static/c8cc71d0c8f7354ccd90fb4c40d79a6b/0c8fb/commerces-session-details.webp 640w", "/client-sdks/static/c8cc71d0c8f7354ccd90fb4c40d79a6b/8e3cb/commerces-session-details.webp 958w"],
                "sizes": "(max-width: 958px) 100vw, 958px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/client-sdks/static/c8cc71d0c8f7354ccd90fb4c40d79a6b/dd4a7/commerces-session-details.png 320w", "/client-sdks/static/c8cc71d0c8f7354ccd90fb4c40d79a6b/0f09e/commerces-session-details.png 640w", "/client-sdks/static/c8cc71d0c8f7354ccd90fb4c40d79a6b/abb65/commerces-session-details.png 958w"],
                "sizes": "(max-width: 958px) 100vw, 958px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/client-sdks/static/c8cc71d0c8f7354ccd90fb4c40d79a6b/abb65/commerces-session-details.png",
                "alt": "commerces session details",
                "title": "commerces session details",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "opacity": "0",
                  "transition": "opacity 0.5s",
                  "color": "inherit",
                  "boxShadow": "inset 0px 0px 0px 400px none",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span></p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Go to the sample application that is installed on your device, and click on the `}<inlineCode parentName="p">{`Assurance`}</inlineCode>{` tab.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Paste the `}<strong parentName="p">{`Assurance Session URL`}</strong>{` that you copied from Assurance, and click `}<inlineCode parentName="p">{`Connect`}</inlineCode>{`.`}</p>
        <p parentName="li"><span parentName="p" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1136px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "105.31249999999999%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "display": "block",
                "transition": "opacity 0.5s 0.5s",
                "pointerEvents": "none"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/client-sdks/static/4554f323e5ad93742ddb6e315594da9a/5530d/commerce-assurance-login.webp 320w", "/client-sdks/static/4554f323e5ad93742ddb6e315594da9a/0c8fb/commerce-assurance-login.webp 640w", "/client-sdks/static/4554f323e5ad93742ddb6e315594da9a/bdd56/commerce-assurance-login.webp 1136w"],
                "sizes": "(max-width: 1136px) 100vw, 1136px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/client-sdks/static/4554f323e5ad93742ddb6e315594da9a/dd4a7/commerce-assurance-login.png 320w", "/client-sdks/static/4554f323e5ad93742ddb6e315594da9a/0f09e/commerce-assurance-login.png 640w", "/client-sdks/static/4554f323e5ad93742ddb6e315594da9a/46e29/commerce-assurance-login.png 1136w"],
                "sizes": "(max-width: 1136px) 100vw, 1136px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/client-sdks/static/4554f323e5ad93742ddb6e315594da9a/46e29/commerce-assurance-login.png",
                "alt": "commerce assurance login",
                "title": "commerce assurance login",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "opacity": "0",
                  "transition": "opacity 0.5s",
                  "color": "inherit",
                  "boxShadow": "inset 0px 0px 0px 400px none",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span></p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Enter the PIN from Assurance and click `}<inlineCode parentName="p">{`Connect`}</inlineCode>{`.`}</p>
        <p parentName="li"><span parentName="p" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "906px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "179.99999999999997%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "display": "block",
                "transition": "opacity 0.5s 0.5s",
                "pointerEvents": "none"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/client-sdks/static/45282f8b53606be4a83e14af8787e592/5530d/commerce-assurance-connection.webp 320w", "/client-sdks/static/45282f8b53606be4a83e14af8787e592/0c8fb/commerce-assurance-connection.webp 640w", "/client-sdks/static/45282f8b53606be4a83e14af8787e592/76c93/commerce-assurance-connection.webp 906w"],
                "sizes": "(max-width: 906px) 100vw, 906px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/client-sdks/static/45282f8b53606be4a83e14af8787e592/dd4a7/commerce-assurance-connection.png 320w", "/client-sdks/static/45282f8b53606be4a83e14af8787e592/0f09e/commerce-assurance-connection.png 640w", "/client-sdks/static/45282f8b53606be4a83e14af8787e592/8cb5d/commerce-assurance-connection.png 906w"],
                "sizes": "(max-width: 906px) 100vw, 906px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/client-sdks/static/45282f8b53606be4a83e14af8787e592/8cb5d/commerce-assurance-connection.png",
                "alt": "commerce assurance connection",
                "title": "commerce assurance connection",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "opacity": "0",
                  "transition": "opacity 0.5s",
                  "color": "inherit",
                  "boxShadow": "inset 0px 0px 0px 400px none",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span></p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Once connected to Assurance, you will see a Platform icon in red color on the top right corner of the app view. The color of the Platform icon becomes gray if the connectivity to Assurance server is lost for any reason. In this case, you want to reconnect to continue to see the session in the UI.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`In the Assurance session, you should now start seeing events populating the Events List. When clicking the `}<inlineCode parentName="p">{`Purchase`}</inlineCode>{` button from the `}<inlineCode parentName="p">{`Edge`}</inlineCode>{` tab, you should see the Experience events sent to Experience Edge. For more details, refer to `}<a parentName="p" {...{
            "href": "../../../edge/edge-network/validation.md"
          }}>{`Event types handled by the AEP Mobile extension`}</a>{`.`}</p>
        <p parentName="li">{`To check the XDM Experience event was successfully validated, check that the `}<inlineCode parentName="p">{`validation for the given event was successful`}</inlineCode>{` message is present in the `}<inlineCode parentName="p">{`service com.adobe.streaming.validation`}</inlineCode>{` event.`}</p>
      </li>
    </ol>
    <h2 {...{
      "id": "queries-in-adobe-experience-platform"
    }}>{`Queries in Adobe Experience Platform`}</h2>
    <p>{`After using the sample demo application to view products and checkout items in a cart, the XDM Experience Events containing the commerce data are sent to Adobe Experience Platform through Experience Edge.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`It may take up to 15-20 minutes before the data shows up in Adobe Experience Platform.`}</p>
    <p>{`Query the dataset which stores the commerce data by doing the following:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Log in to `}<a parentName="p" {...{
            "href": "https://experience.adobe.com/platform"
          }}>{`Adobe Experience Platform`}</a>{` using your Adobe credentials.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Select your Adobe Experience Cloud Organization to the organization ID used to configure the demo application.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`On the navigation panel, under Data Management, select Datasets and select the dataset you created at the beginning of this tutorial. From the right panel, copy the `}<inlineCode parentName="p">{`Table name`}</inlineCode>{` value.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`From the left panel, select `}<strong parentName="p">{`Queries`}</strong>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Select the `}<strong parentName="p">{`Overview`}</strong>{` tab, then select `}<strong parentName="p">{`Create query`}</strong>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`In the text box, enter a SQL query against your dataset table.`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-sql"
          }}>{`SELECT * FROM paste_your_table_name_here WHERE eventType = 'commerce.purchases' LIMIT 10
`}</code></pre>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Select the "Play" icon to run the query. The results will appear in the `}<strong parentName="p">{`Results`}</strong>{` tab at the bottom.`}</p>
      </li>
    </ol>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`You can save this query and run it later when needed.`}</p>
    <h2 {...{
      "id": "implement-add-to-cart-xdm-events"
    }}>{`Implement Add to cart XDM events`}</h2>
    <p>{`For this exercise, implement the "Add to cart functionality" in the sample application. Navigate to `}<inlineCode parentName="p">{`EdgeViewController.swift`}</inlineCode>{` (iOS) / `}<inlineCode parentName="p">{`EdgeTab.java`}</inlineCode>{`(Android) and implement the `}<inlineCode parentName="p">{`sendAddToCartXDMEvent`}</inlineCode>{` function. The recommended eventType is `}<inlineCode parentName="p">{`commerce.productListAdds`}</inlineCode>{`.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`Use the `}<inlineCode parentName="p">{`sendPurchaseXDMEvent`}</inlineCode>{` as an example and Assurance to validate that the XDM Experience Event is properly formatted.`}</p>
    <h2 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h2>
    <p>{`If you would like to explore other XDM schemas for your mobile use-case, find more details in the `}<a parentName="p" {...{
        "href": "../../../edge/edge-network/index.md"
      }}>{`Adobe Experience Platform - Experience Edge`}</a>{` page.`}</p>
    <p>{`To learn more about Experience Data Model (XDM), schemas, datasets, and field groups, read `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/experience-platform/xdm/home.html"
      }}>{`XDM System overview`}</a>{`.`}</p>
    <h2 {...{
      "id": "other-tutorials"
    }}>{`Other tutorials`}</h2>
    <h3 {...{
      "id": "sample-xdm-implementation"
    }}>{`Sample XDM implementation`}</h3>
    <p>{`To see a sample XDM implementation, please read the `}<a parentName="p" {...{
        "href": "./sample-xdm-implementation.md"
      }}>{`sample XDM Implementation tutorial`}</a>{`.`}</p>
    <h3 {...{
      "id": "rules-and-xdm-events"
    }}>{`Rules and XDM events`}</h3>
    <p>{`To learn more about the rules used for XDM events, please read the `}<a parentName="p" {...{
        "href": "./rules-and-xdm-events.md"
      }}>{`rules and XDM events tutorial`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      